import React, { useState } from "react";
import {
  Box,
  Button,
  Heading,
  Sheet,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import {
  hideNativeTabBar,
  IProperty,
  IPropertySelection,
} from "@origin-digital/event-dispatcher";
import { TileButton, TileButtonGroup } from "@origin-digital/ods-tile-button";
import { TileButtonGroupProps } from "@origin-digital/ods-tile-button/dist/typings/TileButtonGroup/TileButtonGroup";
import {
  IconChevronDownSmall,
  IconChevronUpSmall,
} from "@origin-digital/ods-icons";
import { PropertySheetBottomLinks } from "./PropertySheetBottomLinks";
import {
  handleSelectedPropertyAnalytics,
  trackPropertySelectorClose,
} from "./analytics";
import { content } from "./content";

export interface PropertySheetProps {
  selectedProperty: IPropertySelection | null;
  setProperty: (property: IPropertySelection | null) => void;
  activeProperties: IPropertySelection[];
  inactiveProperties: IPropertySelection[];
  open: boolean;
  showAllProperties?: boolean;
  setOpen: (show: boolean) => void;
}

const ALL_PROPERTIES_ID = "all-properties";

const PropertyCard = ({
  property,
  selected = false,
}: {
  property: IProperty | null;
  selected: boolean;
}) => {
  const label = property?.label.toUpperCase() ?? content.allPropertiesLabel;
  const id = property?.id ?? ALL_PROPERTIES_ID;
  return (
    <TileButton
      key={id}
      value={id}
      id={id}
      label={label}
      togglePosition="left"
      selected={selected}
      aria-label={label}
    />
  );
};

export const PropertySheet: React.FC<PropertySheetProps> = ({
  selectedProperty,
  activeProperties,
  inactiveProperties,
  setProperty,
  open = false,
  showAllProperties = true,
  setOpen,
}) => {
  const [showInactive, setShowInactive] = useState(false);
  const hasMultipleProperties =
    activeProperties.length + inactiveProperties.length > 1;

  const mapPropertyCards = (property: IProperty | null) => (
    <PropertyCard
      property={property}
      selected={selectedProperty?.id === property?.id}
    />
  );

  const PropertiesGroup: React.FC<
    Omit<TileButtonGroupProps, "children"> & {
      properties: (IPropertySelection | null)[];
    }
  > = ({ id, properties }) => (
    <TileButtonGroup
      id={id}
      value={selectedProperty?.id ?? ALL_PROPERTIES_ID}
      hideHelperTextSpace={true}
      onChange={(_e, newPropertyId) => {
        const newProperty =
          properties.find((property) => property?.id === newPropertyId) ?? null;
        handleSelectedPropertyAnalytics(
          newProperty?.id,
          activeProperties,
          inactiveProperties
        );
        setProperty(newProperty);
        setOpen(false);
      }}
    >
      {properties.map(mapPropertyCards)}
    </TileButtonGroup>
  );

  return (
    <Sheet
      id="property-sheet"
      title={content.title}
      data-id="property-sheet"
      open={open}
      onClose={() => {
        hideNativeTabBar({ hide: false });
        setOpen(false);
      }}
      dialogMaxWidth="xs"
      onCloseCapture={trackPropertySelectorClose}
    >
      <PropertiesGroup
        id="property-sheet-group"
        // Add null to list of active properties for 'All Properties' if showAllProperties
        properties={[...(showAllProperties ? [null] : []), ...activeProperties]}
      />

      {hasMultipleProperties && (
        <Box padding="medium" paddingBottom="xxsmall">
          <Text align="center" variant="body">
            {content.multipleProperties}
          </Text>
        </Box>
      )}

      {inactiveProperties.length > 0 && !showInactive && (
        <Box paddingTop="medium" paddingBottom="xxsmall">
          <Stack alignX="center">
            <Button
              variant="text"
              size="small"
              color="secondaryB"
              noTextPadding
              iconPosition="trailing"
              icon={<IconChevronDownSmall variant="solid" color="neutral" />}
              onClick={() => setShowInactive(true)}
            >
              {content.show}
            </Button>
          </Stack>
        </Box>
      )}

      {showInactive && (
        <Box
          paddingY="medium"
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
        >
          <Heading variant="h4">{content.previousTitle}</Heading>
          <Button
            variant="text"
            size="small"
            color="secondaryB"
            noTextPadding
            iconPosition="trailing"
            icon={<IconChevronUpSmall variant="solid" color="neutral" />}
            onClick={() => setShowInactive(false)}
          >
            {content.hide}
          </Button>
        </Box>
      )}
      {showInactive && (
        <PropertiesGroup
          id="property-sheet-group"
          properties={inactiveProperties}
        />
      )}

      <PropertySheetBottomLinks closeSheet={() => setOpen(false)} />
    </Sheet>
  );
};
